import React from 'react';
import { CheckCircle2 } from 'lucide-react';
import { companyInfo } from '../mockData';
import { Card, CardContent } from '../components/ui/card';

export const About = () => {
  return (
    <div className="flex flex-col">
      {/* Page Header */}
      <section className="bg-gradient-to-b from-blue-50 to-white py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              À propos de {companyInfo.name}
            </h1>
            <p className="text-lg text-gray-600">
              Votre partenaire de confiance en génie civil
            </p>
          </div>
        </div>
      </section>

      {/* Company Description */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Notre entreprise
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              {companyInfo.description}
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              {companyInfo.mission}
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
              Nos valeurs fondamentales
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {companyInfo.values.map((value, index) => (
                <Card key={index} className="border-none shadow-md hover:shadow-lg transition-shadow duration-300">
                  <CardContent className="pt-6 pb-6">
                    <div className="flex items-start">
                      <div className="flex-shrink-0 mr-4">
                        <CheckCircle2 className="text-blue-600" size={28} />
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">
                          {value.title}
                        </h3>
                        <p className="text-gray-600 leading-relaxed">
                          {value.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Pourquoi nous choisir ?
            </h2>
            <div className="space-y-4">
              <div className="flex items-start">
                <CheckCircle2 className="text-blue-600 flex-shrink-0 mr-3 mt-1" size={24} />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">Expertise locale :</span> Une connaissance approfondie du marché sénégalais et des normes locales
                </p>
              </div>
              <div className="flex items-start">
                <CheckCircle2 className="text-blue-600 flex-shrink-0 mr-3 mt-1" size={24} />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">Équipe qualifiée :</span> Des ingénieurs et techniciens expérimentés et certifiés
                </p>
              </div>
              <div className="flex items-start">
                <CheckCircle2 className="text-blue-600 flex-shrink-0 mr-3 mt-1" size={24} />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">Matériel moderne :</span> Des équipements de pointe pour garantir efficacité et qualité
                </p>
              </div>
              <div className="flex items-start">
                <CheckCircle2 className="text-blue-600 flex-shrink-0 mr-3 mt-1" size={24} />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">Suivi personnalisé :</span> Un accompagnement à chaque étape de votre projet
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Media Section */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Suivez nos projets
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Découvrez nos réalisations en temps réel sur nos réseaux sociaux
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href={companyInfo.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-6 py-3 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg"
            >
              <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
              Contactez-nous sur WhatsApp
            </a>
            <a
              href={companyInfo.tiktok}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-6 py-3 bg-black hover:bg-gray-800 text-white font-semibold rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg"
            >
              <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
              </svg>
              Suivez-nous sur TikTok
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
